import React, { useCallback, useEffect, useMemo, useReducer } from 'react';
import { getStore, getMilanGeoCodes, addCrossStateStores } from '../utils';
import { fetchMilanStores } from '../hooks/fetchMilanStores';
import { fetchGlobalData } from '../hooks/fetchGlobalData';
import { fetchPromotionsBannerData } from '../hooks/fetchPromotionsBannerData';
import { calculateMetrics, METRIC_FORMATTERS } from '../hooks/useMetrics';

export const GlobalSiteDataContext = React.createContext();

const isBrowser = typeof window !== 'undefined';

// Module-level singleton — ensures the three fetches only fire once regardless of
// how many times GlobalContextProvider mounts (Gatsby mounts it in both SSR and browser).
let siteDataPromise = null;

function getSiteDataPromise() {
    if (!isBrowser) return null;
    if (!siteDataPromise) {
        siteDataPromise = Promise.allSettled([fetchMilanStores(), fetchGlobalData(), fetchPromotionsBannerData()]);
    }
    return siteDataPromise;
}

// initialState must be identical between SSR and the client's first render to avoid
// a React hydration mismatch. isBrowser cannot be used here — SSR sees false but the
// client would see true, causing React to force a full re-render on every page (the flicker).
// The client sets isBuilderDataLoading: true via useEffect after hydration completes.
const initialState = {
    milanStores: [],
    globalData: {},
    selectedStore: {},
    promotionalBannersTextData: { grandSpecialsBanner: undefined, specialsBanner: undefined },
    isBuilderDataLoading: false,
};

function reducer(state, action) {
    switch (action.type) {
        case 'SET_LOADING':
            return { ...state, isBuilderDataLoading: true };
        case 'BATCH_LOADED':
            return {
                ...state,
                milanStores: action.milanStores ?? state.milanStores,
                globalData: action.globalData ?? state.globalData,
                selectedStore: action.selectedStore ?? state.selectedStore,
                promotionalBannersTextData: action.promotionalBannersTextData ?? state.promotionalBannersTextData,
                isBuilderDataLoading: false,
            };
        case 'SET_SELECTED_STORE':
            return { ...state, selectedStore: action.selectedStore };
        default:
            return state;
    }
}

export const GlobalContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    useEffect(() => {
        const promise = getSiteDataPromise();
        if (!promise) return;

        // Signal that data is loading — runs after hydration so SSR and the initial
        // client render both see isBuilderDataLoading: false (no hydration mismatch).
        dispatch({ type: 'SET_LOADING' });

        const localStorageStore = isBrowser ? window.localStorage.getItem('selectedStore') : null;

        promise
            .then(([storesResult, globalDataResult, bannerResult]) => {
                if (storesResult.status === 'rejected') console.error('fetchMilanStores failed:', storesResult.reason);
                if (globalDataResult.status === 'rejected')
                    console.error('fetchGlobalData failed:', globalDataResult.reason);
                if (bannerResult.status === 'rejected')
                    console.error('fetchPromotionsBannerData failed:', bannerResult.reason);

                const milanStores = storesResult.status === 'fulfilled' ? addCrossStateStores(storesResult.value) : [];
                const globalData = globalDataResult.status === 'fulfilled' ? globalDataResult.value : {};
                const bannerContent = bannerResult.status === 'fulfilled' ? bannerResult.value : null;

                const selectedStore =
                    localStorageStore && milanStores.length > 0 ? (getStore(localStorageStore, milanStores) ?? {}) : {};

                const promotionalBannersTextData = {
                    grandSpecialsBanner: bannerContent?.data?.grandSpecialsBanner,
                    specialsBanner: bannerContent?.data?.specialsBanner,
                };

                dispatch({
                    type: 'BATCH_LOADED',
                    milanStores,
                    globalData,
                    selectedStore,
                    promotionalBannersTextData,
                });
            })
            .catch((err) => {
                console.error('Failed to load site data:', err);
                dispatch({
                    type: 'BATCH_LOADED',
                    milanStores: [],
                    globalData: {},
                    selectedStore: {},
                    promotionalBannersTextData: { grandSpecialsBanner: undefined, specialsBanner: undefined },
                });
            });
    }, []);

    const { milanStores, globalData, selectedStore, promotionalBannersTextData, isBuilderDataLoading } = state;

    const { metrics, formattedMetrics, milanGeoCodes } = useMemo(() => {
        const m = calculateMetrics(milanStores, globalData);
        return {
            metrics: m,
            formattedMetrics: {
                clinicCount: METRIC_FORMATTERS.clinicCount(m.clinicCount),
                totalReviews: METRIC_FORMATTERS.totalReviews(m.totalReviews),
                stateCount: METRIC_FORMATTERS.stateCount(m.stateCount),
                treatmentsPerMonth: METRIC_FORMATTERS.treatmentsPerMonth(m.treatmentsPerMonth),
            },
            milanGeoCodes: getMilanGeoCodes(milanStores) ?? [],
        };
    }, [milanStores, globalData]);

    const updateSelectedStore = useCallback(
        (salesforceValue) => {
            const store = getStore(salesforceValue, milanStores) ?? {};
            dispatch({ type: 'SET_SELECTED_STORE', selectedStore: store });
            if (isBrowser) window.localStorage.setItem('selectedStore', salesforceValue);
        },
        [milanStores]
    );

    const siteData = useMemo(
        () => ({
            milanStores,
            metrics,
            formattedMetrics,
            globalData,
            selectedStore,
            updateSelectedStore,
            milanGeoCodes,
            promotionalBannersTextData,
            isBuilderDataLoading,
        }),
        [
            milanStores,
            globalData,
            selectedStore,
            promotionalBannersTextData,
            isBuilderDataLoading,
            metrics,
            formattedMetrics,
            milanGeoCodes,
            updateSelectedStore,
        ]
    );

    return <GlobalSiteDataContext.Provider value={siteData}>{children}</GlobalSiteDataContext.Provider>;
};
